import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Archive,
  CheckCircle2,
  Landmark,
  Pencil,
  Plus,
  Printer,
  Trash2,
  UserCircle,
  XCircle,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import type { Asignables, PuntoEmision, TipoDeComprobante } from '@/shared/api/tipos';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { Interruptor } from '@/shared/ui/Interruptor';
import { Pestanas } from '@/shared/ui/Pestanas';
import { ListaSeleccionable } from '@/shared/ui/ListaSeleccionable';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';

const SIN_ASIGNAR = 0;

const USOS: { valor: PuntoEmision['uso']; etiqueta: string }[] = [
  { valor: null, etiqueta: 'Caja de una cajera' },
  { valor: 'EMISION', etiqueta: 'Emisión mensual Art. 9' },
  { valor: 'GENERADORES', etiqueta: 'Generadores' },
  { valor: 'TITULOS', etiqueta: 'Art. 8' },
];

const esquema = z.object({
  codigo: z.string().regex(/^[0-9]{3}$/, 'Deben ser 3 dígitos, por ejemplo 001'),
  codigoEstablecimiento: z.string().regex(/^[0-9]{3}$/, 'Deben ser 3 dígitos, por ejemplo 001'),
  nombre: z.string().min(1, 'El nombre es obligatorio').max(120),
  usuarioId: z.coerce.number().int().min(0),
});

type Formulario = z.infer<typeof esquema>;

const secuencialSri = (n: number) => String(n).padStart(9, '0');

export function PaginaPuntosEmision() {
  const clienteDeConsultas = useQueryClient();

  const [editando, setEditando] = useState<PuntoEmision | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<PuntoEmision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vista, setVista] = useState<'en-uso' | 'historicos'>('en-uso');

  const puntos = useQuery({
    queryKey: ['puntos-emision'],
    queryFn: () => api.get<PuntoEmision[]>('/puntos-emision'),
  });

  const enUso = (puntos.data ?? []).filter((p) => !p.historico);
  const historicos = (puntos.data ?? []).filter((p) => p.historico);

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['puntos-emision'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: ['puntos-emision-asignables'] });
  }

  function alFallar(causa: unknown, respaldo: string) {
    setError(causa instanceof ErrorDeApi ? causa.message : respaldo);
  }

  const cambiarUso = useMutation({
    mutationFn: ({ id, uso }: { id: number; uso: PuntoEmision['uso'] }) =>
      api.put<PuntoEmision>(`/puntos-emision/${id}/uso`, { uso }),
    onSuccess: () => {
      refrescar();
      notificar.exito('Ya sale por aquí lo que le toca', 'Uso cambiado');
    },
    onError: (e) => alFallar(e, 'No se pudo cambiar el uso'),
  });

  const aHistorico = useMutation({
    mutationFn: (id: number) => api.post<PuntoEmision>(`/puntos-emision/${id}/historico`),
    onSuccess: (p) => {
      refrescar();
      notificar.exito('Queda en Históricos, solo para consultar', p.nombre);
    },
    onError: (e) => alFallar(e, 'No se pudo pasar a histórico'),
  });

  const cambiarNotas = useMutation({
    mutationFn: ({ id, deNotas }: { id: number; deNotas: boolean }) =>
      api.put<PuntoEmision>(`/puntos-emision/${id}/notas`, { deNotas }),
    onSuccess: (p) => {
      refrescar();
      notificar.exito(
        p.deNotas ? 'Solo este punto hace las notas de crédito' : 'Ya no hace las notas de crédito',
        p.nombre,
      );
    },
    onError: (e) => alFallar(e, 'No se pudo cambiar'),
  });

  const cambiarEstado = useMutation({
    mutationFn: (p: PuntoEmision) =>
      api.put<PuntoEmision>(`/puntos-emision/${p.id}`, {
        codigo: p.codigo,
        codigoEstablecimiento: p.codigoEstablecimiento,
        nombre: p.nombre,
        usuarioId: p.usuarioId,

        comprobantes: p.comprobantes.map((c) => ({
          tipo: c.tipo,
          secuencial: c.secuencial,
          habilitado: c.habilitado,
        })),
        activo: !p.activo,
      }),
    onSuccess: refrescar,
    onError: (e) => alFallar(e, 'No se pudo cambiar el estado'),
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/puntos-emision/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
    },
    onError: (e) => alFallar(e, 'No se pudo eliminar'),
  });

  return (
    <>
      <EncabezadoDePagina
        icono={Printer}
        color="violeta"
        titulo="Puntos de emisión"
        subtitulo="Gestión de cajas y puntos de facturación SRI"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nuevo punto de emisión"
            aria-label="Nuevo punto de emisión"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      <div className="mb-6 rounded-xl border border-violet-200 bg-violet-50 p-4">
        <p className="text-sm uppercase text-violet-900">
          <span className="font-semibold">Formato del SRI:</span> cada comprobante se identifica
          como{' '}
          <code className="importe rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-900">
            establecimiento-punto-secuencial
          </code>
          . Cada caja lleva una serie <b>por tipo</b>: la nota de crédito 000000001 no tiene nada
          que ver con la factura del mismo número.
        </p>
      </div>

      <Pestanas
        etiqueta="Qué puntos"
        activa={vista}
        alElegir={setVista}
        pestanas={[
          {
            clave: 'en-uso',
            rotulo: `En uso (${enUso.length})`,
            descripcion: 'Los que emiten',
            icono: Printer,
          },
          {
            clave: 'historicos',
            rotulo: `Históricos (${historicos.length})`,
            descripcion: 'Series cerradas, solo consulta',
            icono: Archive,
          },
        ]}
      />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {puntos.isPending ? (
          <div className="col-span-full">
            <Cargando titulo="Cargando las cajas" detalle="Obteniendo registros" alto="h-64" />
          </div>
        ) : vista === 'historicos' ? (
          historicos.length === 0 ? (
            <p className="col-span-full rounded-xl border border-armazon-200 bg-white p-10 text-center text-sm uppercase text-armazon-400">
              No hay puntos históricos
            </p>
          ) : (
            historicos.map((p) => <TarjetaHistorica key={p.id} punto={p} />)
          )
        ) : enUso.length === 0 ? (
          <div className="col-span-full rounded-xl border border-armazon-200 bg-white p-12 text-center">
            <Printer className="mx-auto mb-3 size-12 text-armazon-200" aria-hidden />
            <p className="text-sm font-medium uppercase text-armazon-500">
              No hay puntos de emisión configurados
            </p>
            <p className="mt-1 text-xs uppercase text-armazon-400">Cree uno para poder facturar</p>
          </div>
        ) : (
          enUso.map((p) => (
            <Tarjeta
              key={p.id}
              punto={p}
              alEditar={() => setEditando(p)}
              alEliminar={() => setAEliminar(p)}
              alAlternar={() => cambiarEstado.mutate(p)}
              alCambiarUso={(uso) => cambiarUso.mutate({ id: p.id, uso })}
              alCambiarNotas={() => cambiarNotas.mutate({ id: p.id, deNotas: !p.deNotas })}
              alPasarAHistorico={() => aHistorico.mutate(p.id)}
              hayPuntoDeNotas={enUso.some((otro) => otro.deNotas)}
              notasDeSuResponsable={
                !p.deNotas && p.usuarioId
                  ? enUso.find((otro) => otro.deNotas && otro.usuarioId === p.usuarioId)
                  : undefined
              }
              cambiando={
                cambiarEstado.isPending ||
                cambiarUso.isPending ||
                cambiarNotas.isPending ||
                aHistorico.isPending
              }
            />
          ))
        )}
      </div>

      <FormularioDePunto
        key={editando?.id ?? 'nuevo'}
        abierto={creando || editando !== null}
        punto={editando}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar punto de emisión"
        descripcion="Dejará de poder cobrarse desde:"
        detalle={aEliminar ? `${aEliminar.identificadorSri} · ${aEliminar.nombre}` : undefined}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

function Tarjeta({
  punto,
  alEditar,
  alEliminar,
  alAlternar,
  alCambiarUso,
  alCambiarNotas,
  alPasarAHistorico,
  hayPuntoDeNotas,
  notasDeSuResponsable,
  cambiando,
}: {
  punto: PuntoEmision;
  alEditar: () => void;
  alEliminar: () => void;
  alAlternar: () => void;
  alCambiarUso: (uso: PuntoEmision['uso']) => void;
  alCambiarNotas: () => void;
  alPasarAHistorico: () => void;
  hayPuntoDeNotas: boolean;
  notasDeSuResponsable?: PuntoEmision | undefined;
  cambiando: boolean;
}) {
  const [confirmando, setConfirmando] = useState(false);
  return (
    <article
      className={[
        'flex flex-col overflow-hidden rounded-xl border bg-white shadow-sm',
        punto.activo ? 'border-armazon-200' : 'border-red-200 bg-red-50/30',
      ].join(' ')}
    >
      <header className="flex items-center justify-between gap-2 border-b border-armazon-100 px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={[
              'grid size-10 shrink-0 place-items-center rounded-xl',
              punto.activo ? 'bg-violet-50 text-violet-500' : 'bg-red-50 text-red-400',
            ].join(' ')}
          >
            <Printer className="size-[18px]" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold uppercase text-armazon-900">
              {punto.nombre}
            </h2>
            <span className="importe rounded bg-armazon-100 px-1.5 py-0.5 text-xs text-armazon-600">
              {punto.identificadorSri}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={alEditar}
            title="Editar"
            aria-label={`Editar ${punto.nombre}`}
            className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
          >
            <Pencil className="size-[15px]" aria-hidden />
          </button>
          <button
            type="button"
            onClick={alEliminar}
            disabled={punto.emitio}
            title={
              punto.emitio
                ? 'No se puede eliminar: ya emitió comprobantes. Desactívelo en su lugar.'
                : 'Eliminar'
            }
            aria-label={`Eliminar ${punto.nombre}`}
            className="rounded-lg p-1.5 text-armazon-400 transition-colors enabled:hover:bg-red-50 enabled:hover:text-alerta disabled:cursor-not-allowed disabled:text-armazon-200"
          >
            <Trash2 className="size-[15px]" aria-hidden />
          </button>
        </div>
      </header>

      <div className="grid gap-3 px-5 py-4">
        <Dato etiqueta="Responsable">
          {punto.usuarioNombre ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-armazon-800">
              <UserCircle className="size-3.5 text-violet-400" aria-hidden />
              {punto.usuarioNombre}
            </span>
          ) : punto.uso === 'EMISION' || punto.uso === 'GENERADORES' ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-armazon-600">
              <Landmark className="size-3.5 text-violet-400" aria-hidden />
              {punto.uso === 'EMISION' ? 'Emisión mensual Art. 9' : 'Generadores'}
            </span>
          ) : punto.esDeTitulos ? (
            // No le falta nadie: por aqui no se sienta una cajera. Las tres cobran los
            // titulos desde su ventanilla y el recibo sale con el codigo de SU caja; este
            // punto solo da el numero de la factura electronica. Pintarlo en ambar como
            // «Sin asignar» hacia que pareciera un pendiente de configuracion.
            <span className="flex items-center gap-1.5 text-xs font-medium text-armazon-600">
              <Landmark className="size-3.5 text-violet-400" aria-hidden />
              Recaudación del Art. 8
            </span>
          ) : (
            <span className="text-xs font-medium uppercase text-aviso">Sin asignar</span>
          )}
        </Dato>

        <Dato etiqueta="Uso">
          {!punto.comprobantes.some((c) => c.tipo === '01' && c.habilitado) ? (
            <span className="text-xs font-medium uppercase text-armazon-600">
              {punto.deNotas ? 'Notas de crédito' : 'Sin facturas'}
            </span>
          ) : (
            <select
              value={punto.uso ?? ''}
              disabled={cambiando}
              onChange={(e) => alCambiarUso((e.target.value || null) as PuntoEmision['uso'])}
              className="h-7 rounded-md border border-armazon-200 bg-white px-1.5 text-[11px] uppercase text-armazon-700 disabled:opacity-50"
            >
              {USOS.map((u) => (
                <option key={u.valor ?? 'caja'} value={u.valor ?? ''}>
                  {u.etiqueta}
                </option>
              ))}
            </select>
          )}
        </Dato>

        {punto.comprobantes.some((c) => c.tipo === '04' && c.habilitado) && (
          <Dato etiqueta="Hace las notas de crédito">
            <Interruptor
              activo={punto.deNotas}
              alCambiar={alCambiarNotas}
              etiqueta={`Que ${punto.nombre} haga las notas de crédito`}
              deshabilitado={cambiando || !punto.activo}
            />
          </Dato>
        )}

        <Dato etiqueta="Estado">
          <button
            type="button"
            onClick={alAlternar}
            disabled={cambiando}
            title={
              punto.activo
                ? 'Pulse para desactivar esta caja'
                : 'Pulse para volver a activar esta caja'
            }
            className={[
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium uppercase transition-colors disabled:opacity-50',
              punto.activo
                ? 'border-marca-200 bg-marca-50 text-marca-700 hover:border-marca-400 hover:bg-marca-100'
                : 'border-red-200 bg-red-50 text-alerta hover:border-red-400 hover:bg-red-100',
            ].join(' ')}
          >
            {punto.activo ? (
              <CheckCircle2 className="size-3" aria-hidden />
            ) : (
              <XCircle className="size-3" aria-hidden />
            )}
            {punto.activo ? 'Activo' : 'Inactivo'}
          </button>
        </Dato>
      </div>

      {!punto.activo && (
        <div className="border-t border-red-100 px-5 py-3">
          {confirmando ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase leading-tight text-red-800">
                Su serie se cierra para siempre. ¿Seguro?
              </span>
              <span className="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => setConfirmando(false)}
                  className="h-7 rounded-md border border-armazon-200 bg-white px-2 text-[10px] font-semibold uppercase text-armazon-600"
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={alPasarAHistorico}
                  disabled={cambiando}
                  className="h-7 rounded-md bg-red-600 px-2 text-[10px] font-bold uppercase text-white disabled:opacity-50"
                >
                  Sí, pasar
                </button>
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase text-red-700 hover:underline"
            >
              <Archive className="size-3.5" aria-hidden />
              Pasar a histórico
            </button>
          )}
        </div>
      )}

      <footer className="mt-auto border-t border-armazon-100 bg-armazon-50/60 px-5 py-3">
        <p className="mb-2 text-[11px] uppercase tracking-wide text-armazon-500">
          Próximo comprobante
        </p>
        <div className="grid gap-1.5">
          {punto.comprobantes
            .filter((c) => c.tipo !== '04' || !hayPuntoDeNotas || punto.deNotas)
            .map((c) => (
              <div key={c.tipo} className="flex items-center justify-between gap-2">
                <span
                  className={`truncate text-xs uppercase ${
                    c.habilitado ? 'text-armazon-700' : 'text-armazon-400 line-through'
                  }`}
                >
                  {c.nombre}
                </span>
                {c.habilitado ? (
                  <span className="importe shrink-0 text-xs font-semibold text-armazon-800">
                    {c.proximoNumero}
                  </span>
                ) : (
                  <span className="shrink-0 text-[10px] uppercase text-armazon-400">
                    Suspendido
                  </span>
                )}
              </div>
            ))}
          {notasDeSuResponsable?.comprobantes
            .filter((c) => c.tipo === '04' && c.habilitado)
            .map((c) => (
              <div
                key="notas"
                className="flex items-center justify-between gap-2"
                title="Las notas de crédito de su responsable salen por el punto de notas"
              >
                <span className="truncate text-xs uppercase text-armazon-400">
                  {c.nombre} · por el {notasDeSuResponsable.codigo}
                </span>
                <span className="importe shrink-0 text-xs text-armazon-400">{c.proximoNumero}</span>
              </div>
            ))}
        </div>
      </footer>
    </article>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs uppercase text-armazon-600">{etiqueta}</span>
      {children}
    </div>
  );
}

interface EstadoDeTipo {
  marcado: boolean;
  secuencial: string;

  emitio: boolean;
}

function FormularioDePunto({
  abierto,
  punto,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  punto: PuntoEmision | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = punto !== null;
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const tipos = useQuery({
    queryKey: ['tipos-de-comprobante'],
    queryFn: () => api.get<TipoDeComprobante[]>('/puntos-emision/tipos-de-comprobante'),

    staleTime: Infinity,
  });

  const asignables = useQuery({
    queryKey: ['puntos-emision-asignables', punto?.id ?? null],
    queryFn: () =>
      api.get<Asignables>('/puntos-emision/asignables', { query: { puntoActual: punto?.id } }),
    enabled: abierto,
  });

  const [comprobantes, setComprobantes] = useState<Record<string, EstadoDeTipo>>(() =>
    Object.fromEntries(
      (punto?.comprobantes ?? []).map((c) => [
        c.tipo,
        { marcado: c.habilitado, secuencial: String(c.secuencial), emitio: c.emitio },
      ]),
    ),
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      codigo: punto?.codigo ?? '',
      codigoEstablecimiento: punto?.codigoEstablecimiento ?? '001',
      nombre: punto?.nombre ?? '',
      usuarioId: punto?.usuarioId ?? SIN_ASIGNAR,
    },
  });

  const responsable = watch('usuarioId');
  const establecimiento = watch('codigoEstablecimiento');
  const codigo = watch('codigo');

  const alMenosUno = Object.values(comprobantes).some((c) => c.marcado);

  function alternarTipo(tipo: string) {
    setErrorGeneral(null);
    setComprobantes((previos) => {
      const actual = previos[tipo] ?? { marcado: false, secuencial: '0', emitio: false };
      return { ...previos, [tipo]: { ...actual, marcado: !actual.marcado } };
    });
  }

  function cambiarSecuencial(tipo: string, valor: string) {
    setComprobantes((previos) => ({
      ...previos,
      [tipo]: {
        marcado: previos[tipo]?.marcado ?? true,
        emitio: previos[tipo]?.emitio ?? false,
        secuencial: valor,
      },
    }));
  }

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);

    if (!alMenosUno) {
      setErrorGeneral('Indique al menos un tipo de comprobante que esta caja pueda emitir.');
      return;
    }

    try {
      const lista = Object.entries(comprobantes)
        .filter(([, c]) => c.marcado || c.emitio)
        .map(([tipo, c]) => ({
          tipo,
          secuencial: Number(c.secuencial || 0),
          habilitado: c.marcado,
        }));

      const cuerpo = {
        codigo: datos.codigo,
        codigoEstablecimiento: datos.codigoEstablecimiento,
        nombre: datos.nombre,
        usuarioId: datos.usuarioId === SIN_ASIGNAR ? null : datos.usuarioId,
        comprobantes: lista,
        activo: punto?.activo ?? true,
      };

      if (editando) {
        await api.put<PuntoEmision>(`/puntos-emision/${punto.id}`, cuerpo);
      } else {
        await api.post<PuntoEmision>('/puntos-emision', cuerpo);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Punto de emisión ${editando ? 'actualizado' : 'guardado'}`);
    } catch (causa) {
      setErrorGeneral(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el punto de emisión',
      );
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el punto de emisión',
      );
    }
  });

  const serieValida = /^[0-9]{3}$/.test(establecimiento ?? '') && /^[0-9]{3}$/.test(codigo ?? '');

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar punto de emisión' : 'Nuevo punto de emisión'}
      descripcion={editando ? punto.identificadorSri : 'Una caja o ventanilla de cobro'}
      icono={Printer}
      alEnviar={enviar}
      enviando={isSubmitting}
    >
      {errorGeneral && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {errorGeneral}
        </p>
      )}

      <DosColumnas>
        <CampoDelPanel
          etiqueta="Establecimiento"
          requerido
          error={errors.codigoEstablecimiento?.message}
          ayuda="3 dígitos"
        >
          <input
            {...register('codigoEstablecimiento')}
            inputMode="numeric"
            maxLength={3}
            className={`${claseEntradaPanel} importe`}
          />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Punto" requerido error={errors.codigo?.message} ayuda="3 dígitos">
          <input
            {...register('codigo')}
            inputMode="numeric"
            maxLength={3}
            className={`${claseEntradaPanel} importe`}
            autoFocus
          />
        </CampoDelPanel>
      </DosColumnas>

      <CampoDelPanel
        etiqueta="Nombre"
        requerido
        error={errors.nombre?.message}
        ayuda="Como se conoce la caja: «Ventanilla 1», «Caja del mercado»"
      >
        <input {...register('nombre')} className={claseEntradaPanel} />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Comprobantes que emite"
        requerido
        ayuda="Cada tipo lleva su propia serie ante el SRI"
      >
        <div className="grid gap-2">
          {(tipos.data ?? []).map((t) => {
            const estado = comprobantes[t.codigo];
            const marcado = estado?.marcado ?? false;
            const secuencial = estado?.secuencial ?? '0';

            return (
              <div
                key={t.codigo}
                className={[
                  'rounded-xl border p-3 transition-colors',
                  marcado ? 'border-marca-300 bg-marca-50/40' : 'border-armazon-200',
                ].join(' ')}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium uppercase text-armazon-800">{t.nombre}</p>
                    <p className="importe text-[10px] uppercase text-armazon-500">
                      Código {t.codigo}
                    </p>
                  </div>
                  <Interruptor
                    activo={marcado}
                    alCambiar={() => alternarTipo(t.codigo)}
                    etiqueta={`Emitir ${t.nombre}`}
                  />
                </div>

                {marcado && (
                  <div className="mt-3 grid gap-1.5 border-t border-armazon-200/70 pt-3">
                    <label className="grid gap-1.5">
                      <span className="text-[11px] uppercase tracking-wider text-armazon-600">
                        Último emitido
                      </span>
                      <input
                        type="number"
                        min={estado?.emitio ? Number(secuencial) : 0}
                        value={secuencial}
                        onChange={(e) => cambiarSecuencial(t.codigo, e.target.value)}
                        className={`${claseEntradaPanel} importe`}
                      />
                    </label>
                    {serieValida && (
                      <p className="importe text-[11px] text-marca-700">
                        Próximo: {establecimiento}-{codigo}-
                        {secuencialSri(Number(secuencial || 0) + 1)}
                      </p>
                    )}
                  </div>
                )}

                {!marcado && estado?.emitio && (
                  <p className="mt-2 text-[11px] uppercase text-aviso">
                    Suspendido · su serie ({secuencial}) se conserva
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Responsable"
        error={errors.usuarioId?.message}
        ayuda={
          asignables.data
            ? `Solo usuarios con rol «${asignables.data.rol}» · cada uno atiende como mucho una caja`
            : 'Cada usuario atiende como mucho una caja'
        }
      >
        <ListaSeleccionable
          nombre="responsable"
          valor={responsable}
          alElegir={(v) => setValue('usuarioId', v, { shouldValidate: true })}
          vacio={
            asignables.data
              ? `No hay usuarios libres con rol «${asignables.data.rol}»`
              : 'Cargando…'
          }
          opciones={[
            {
              valor: SIN_ASIGNAR,
              titulo: 'Sin asignar',
              descripcion: 'La caja queda libre',
            },
            ...(asignables.data?.usuarios ?? []).map((u) => ({
              valor: u.id,
              titulo: u.nombre,
              descripcion: `${u.correo} · ${u.rol}`,
            })),
          ]}
        />
      </CampoDelPanel>
    </PanelLateral>
  );
}

function TarjetaHistorica({ punto }: { punto: PuntoEmision }) {
  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-armazon-200 bg-armazon-50/60">
      <header className="flex items-center gap-3 border-b border-armazon-100 px-5 py-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-armazon-100 text-armazon-400">
          <Archive className="size-[18px]" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold uppercase text-armazon-600">
            {punto.nombre}
          </h2>
          <span className="importe rounded bg-armazon-100 px-1.5 py-0.5 text-xs text-armazon-500">
            {punto.identificadorSri}
          </span>
        </div>
        <span className="ml-auto shrink-0 rounded-full border border-armazon-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase text-armazon-500">
          Serie cerrada
        </span>
      </header>
      <footer className="px-5 py-3">
        <p className="mb-2 text-[11px] uppercase tracking-wide text-armazon-500">Último emitido</p>
        <div className="grid gap-1.5">
          {punto.comprobantes
            .filter((c) => c.secuencial > 0)
            .map((c) => (
              <div key={c.tipo} className="flex items-center justify-between gap-2">
                <span className="truncate text-xs uppercase text-armazon-600">{c.nombre}</span>
                <span className="importe shrink-0 text-xs font-semibold text-armazon-700">
                  {punto.identificadorSri}-{secuencialSri(c.secuencial)}
                </span>
              </div>
            ))}
        </div>
      </footer>
    </article>
  );
}
